<template>
    <v-dialog
        v-model="dialogVisible"
        max-width="550px"
        role="dialog"
        aria-labelledby="confirmation-dialog-title"
        aria-describedby="confirmation-dialog-description"
        persistent
    >
        <v-card>
            <v-card-title class="pb-0 pt-4">
                <h2 id="confirmation-dialog-title">Confirm Payment Information</h2>
            </v-card-title>

            <v-card-text class="pt-1">
                <p
                    id="confirmation-dialog-description"
                    class="mb-4"
                >
                    Please review your payment information before proceeding.
                </p>

                <v-card
                    outlined
                    role="region"
                    aria-label="Payment summary"
                >
                    <v-card-text>
                        <div v-if="isCreditCard">
                            <div class="mb-2">
                                <strong>Payment Method:</strong> Credit Card
                            </div>

                            <div class="mb-2">
                                <strong>Name on Card:</strong> {{ creditCardInfo.name_on_card }}
                            </div>

                            <div class="mb-2">
                                <strong>Card Number:</strong> {{ creditCardInfo.plain_text_card_number }}
                            </div>

                            <div class="mb-2">
                                <strong>Expiration:</strong> {{ expiration }}
                            </div>

                            <div class="mb-2">
                                <strong>CVV:</strong> {{ creditCardInfo.plain_text_ccv }}
                            </div>

                            <div class="mb-2">
                                <strong>Billing Zip Code:</strong> {{ creditCardInfo.billing_zip_code }}
                            </div>
                        </div>

                        <div v-if="isACH">
                            <div class="mb-2">
                                <strong>Payment Method:</strong> Bank Transfer (ACH)
                            </div>

                            <div class="mb-2">
                                <strong>Account Holder Name:</strong> {{ achInfo.account_holder_name }}
                            </div>

                            <div class="mb-2">
                                <strong>Bank Name:</strong> {{ achInfo.bank_name }}
                            </div>

                            <div class="mb-2">
                                <strong>Account Type:</strong> {{ formatAccountType(achInfo.account_type) }}
                            </div>

                            <div class="mb-2">
                                <strong>Routing Number:</strong> {{ achInfo.routing_number }}
                            </div>

                            <div>
                                <strong>Account Number:</strong> {{ achInfo.account_number }}
                            </div>
                        </div>

                        <!-- shared fields for both payment types -->
                        <div
                            v-if="currentEmailAddress"
                            class="my-2"
                        >
                            <strong>Email:</strong> {{ currentEmailAddress }}
                        </div>

                        <div
                            v-if="currentPhoneNumber"
                            class="mt-2"
                        >
                            <strong>Phone:</strong> {{ $rc.pretty(currentPhoneNumber) }}
                        </div>
                    </v-card-text>
                </v-card>
            </v-card-text>

            <v-card-actions class="d-flex flex-wrap justify-space-between gap-3 pt-0 px-6 pb-4">
                <v-btn
                    class="flex-grow-1"
                    aria-label="Cancel and edit payment information"
                    outlined
                    @click="cancelPayment"
                >
                    Edit Payment Info
                </v-btn>

                <v-btn
                    color="secondary"
                    class="white--text flex-grow-1 ml-0"
                    @click="confirmPayment"
                >
                    Confirm and Submit
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    props: {
        value: {
            type: Boolean,
            default: false
        },

        payment: {
            type: Object,
            required: false
        },

        pending_payment_info: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            min_age_in_years: this.payment?.request?.age_verification_minimum_age || 18
        };
    },

    computed: {
        dialogVisible: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        },

        isCreditCard() {
            return this.pending_payment_info?.payment_info_credit_card != null;
        },

        isACH() {
            return this.pending_payment_info?.payment_info_ach != null;
        },

        creditCardInfo() {
            return this.pending_payment_info?.payment_info_credit_card || {};
        },

        achInfo() {
            return this.pending_payment_info?.payment_info_ach || {};
        },

        expiration() {
            // comes in format of full month names - convert back to MM/YY to reflect entry
            if (this.isCreditCard && this.creditCardInfo.expiration_month && this.creditCardInfo.expiration_year) {
                const monthNum = this.getMonthNumber(this.creditCardInfo.expiration_month);
                return `${String(monthNum).padStart(2, '0')}/${String(this.creditCardInfo.expiration_year).slice(-2)}`;
            }
            return '';
        },

        currentEmailAddress() {
            if (this.isCreditCard) {
                return this.creditCardInfo.email_address;
            }
            if (this.isACH) {
                return this.achInfo.email_address;
            }
            return null;
        },

        currentPhoneNumber() {
            if (this.isCreditCard) {
                return this.creditCardInfo.phone_number;
            }
            if (this.isACH) {
                return this.achInfo.phone_number;
            }
            return null;
        },
    },

    methods: {
        getMonthNumber(monthName) {
            const months = {
                'JANUARY': 1,
                'FEBRUARY': 2,
                'MARCH': 3,
                'APRIL': 4,
                'MAY': 5,
                'JUNE': 6,
                'JULY': 7,
                'AUGUST': 8,
                'SEPTEMBER': 9,
                'OCTOBER': 10,
                'NOVEMBER': 11,
                'DECEMBER': 12
            };
            return months[monthName] || 1;
        },

        formatAccountType(accountType) {
            if (!accountType) return '';

            return accountType
                .split('-')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
        },

        confirmPayment() {
            this.$emit('confirmed');
            this.dialogVisible = false;
        },

        cancelPayment() {
            this.$emit('cancelled');
            this.dialogVisible = false;
        },
    }
};
</script>